import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  SelectSlotDialogData,
  SelectSlotDialogResult,
  SlotDayGroup,
  SlotOption,
} from './fulle-select-slot.interface';

/**
 * ===============================================================
 * FulleSelectSlotDialogComponent
 * ===============================================================
 *
 * RESPONSABILITÉ :
 * ---------------------------------------------------------------
 * Permettre à l’utilisateur de sélectionner un créneau horaire
 * pour une méthode de vente donnée.
 *
 * STRATÉGIE UX :
 * ---------------------------------------------------------------
 * 1. L’utilisateur choisit d’abord une date (mat-select).
 * 2. Ensuite il choisit un créneau horaire (mat-select).
 *
 * Pourquoi cette approche ?
 * - Évite l'affichage de 60+ boutons
 * - Meilleure ergonomie mobile
 * - Compatible international (12h / 24h)
 *
 * CONTRAT :
 * ---------------------------------------------------------------
 * À la sélection d’un créneau, le dialog se ferme immédiatement
 * et retourne :
 *    { start: Date }
 *
 * Le composant parent est responsable du traitement métier.
 */

@Component({
  selector: 'app-select-slot-dialog',
  templateUrl: './fulle-select-slot.component.html',
})
export class FulleSelectSlotDialogComponent {

  /**
   * Index du jour actuellement sélectionné.
   * Correspond à l’index dans data.groups.
   */
  dayIndex = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectSlotDialogData,
    private dialogRef: MatDialogRef<FulleSelectSlotDialogComponent>,
  ) {
    /**
     * Sélection automatique du premier jour ayant des créneaux.
     *
     * Cela évite de tomber sur un jour vide.
     */
    const firstWithSlots = data.groups.findIndex(
      g => (g.slots?.length ?? 0) > 0
    );

    this.dayIndex = firstWithSlots >= 0 ? firstWithSlots : 0;
  }

  /**
   * Indique si au moins un groupe de créneaux est disponible.
   */
  get hasGroups(): boolean {
    return (this.data.groups?.length ?? 0) > 0;
  }

  /**
   * Retourne le groupe correspondant au jour sélectionné.
   */
  get selectedGroup(): SlotDayGroup | null {
    return this.data.groups?.[this.dayIndex] ?? null;
  }

  /**
   * Raccourci UX :
   * Permet de sélectionner automatiquement le premier créneau
   * disponible (souvent le plus proche dans le temps).
   *
   * ATTENTION :
   * On suppose que les groups et slots sont déjà triés
   * chronologiquement côté backend.
   */
  pickNext(): void {
    const next = this.data.groups?.flatMap(g => g.slots ?? [])[0];
    if (next) this.pick(next);
  }

  /**
   * Change le jour sélectionné.
   */
  setDay(i: number): void {
    this.dayIndex = i;
  }

  /**
   * Sélection d’un créneau.
   *
   * Ferme immédiatement le dialog.
   */
  pick(slot: SlotOption): void {
    this.dialogRef.close({ start: slot.start } as SelectSlotDialogResult);
  }

  /**
   * Fermeture sans sélection.
   */
  close(): void {
    this.dialogRef.close(null);
  }

  /**
   * trackBy pour éviter les re-render inutiles.
   *
   * IMPORTANT :
   * Angular re-render toute liste sans trackBy.
   */
  trackByGroup = (_: number, g: SlotDayGroup) =>
    g.date?.toString?.() ?? _;

  trackBySlot = (_: number, s: SlotOption) =>
    s.start?.toString?.() ?? _;

  /**
   * Génère un label UX pour un jour donné.
   *
   * - Aujourd’hui
   * - Demain
   * - sinon date formatée
   */
  dayLabel(g: SlotDayGroup): string {
    const d = this.startOfDay(g.date);
    const today = this.startOfDay(new Date());

    const diffDays = Math.round(
      (d.getTime() - today.getTime()) / 86400000
    );

    if (diffDays === 0) return 'Aujourd’hui';
    if (diffDays === 1) return 'Demain';

    return '';
  }

  /**
   * Normalise une date à minuit.
   */
  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }
}